/**
 * Client Portal Auth Store
 *
 * Store Zustand pour l'authentification du portail client
 * Séparé du store auth principal de l'application
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type { ClientPortalUser } from '../types/client-portal.types';
import {
  clientPortalAuthService,
  getPortalToken,
  clearPortalToken,
} from '../services/client-portal.service';

interface ClientPortalAuthState {
  user: ClientPortalUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: ClientPortalUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: ClientPortalUser) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
}

export const useClientPortalAuthStore = create<ClientPortalAuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        isAuthenticated: !!getPortalToken(),
        isLoading: false,
        error: null,

        setUser: (user) => set({ user }),

        setLoading: (isLoading) => set({ isLoading }),

        setError: (error) => set({ error }),

        login: (user) => {
          set({
            user,
            isAuthenticated: true,
            error: null,
          });
        },

        logout: async () => {
          try {
            await clientPortalAuthService.logout();
          } catch (error) {
            // Logout error silently handled - clearing state anyway
          } finally {
            clearPortalToken();
            set({
              user: null,
              isAuthenticated: false,
              error: null,
            });
          }
        },

        checkAuth: async () => {
          const token = getPortalToken();

          if (!token) {
            set({ isAuthenticated: false, user: null });
            return false;
          }

          set({ isLoading: true });

          try {
            const user = await clientPortalAuthService.getMe();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            return true;
          } catch (error) {
            clearPortalToken();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: 'Session expirée',
            });
            return false;
          }
        },

        refreshUser: async () => {
          try {
            const user = await clientPortalAuthService.getMe();
            set({ user });
          } catch (error) {
            // Failed to refresh user - keep existing state
          }
        },
      }),
      {
        name: 'client-portal-auth-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          user: state.user,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            state.isAuthenticated = !!getPortalToken();
          }
        },
      }
    ),
    { name: 'ClientPortalAuth' }
  )
);
